import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Rocket, Shield } from "lucide-react";

interface DeployConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  shopName: string;
  loading?: boolean;
}

export function DeployConsentModal({
  open,
  onOpenChange,
  onConfirm,
  shopName,
  loading,
}: DeployConsentModalProps) {
  const [consented, setConsented] = useState(false);

  function handleConfirm() {
    if (!consented) return;
    onConfirm();
    setConsented(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Publicar Vitrine</DialogTitle>
          <DialogDescription className="text-center">
            Antes de publicar a vitrine de <strong>{shopName}</strong>, confirme
            que os produtos estão em conformidade com a LGPD.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>Ao publicar sua vitrine, você declara que:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                Os produtos exibidos não contêm dados pessoais de terceiros
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                As imagens e descrições respeitam os direitos de imagem e
                propriedade intelectual
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                A vitrine está em conformidade com os termos da{" "}
                <a
                  href="/privacidade/politica-de-privacidade.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
                >
                  Política de Privacidade
                </a>
              </span>
            </li>
          </ul>

          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="deploy-consent"
              checked={consented}
              onCheckedChange={(v) => setConsented(v === true)}
            />
            <label
              htmlFor="deploy-consent"
              className="text-xs leading-tight text-muted-foreground cursor-pointer"
            >
              Eu confirmo que os produtos da vitrine estão em conformidade com a LGPD
              e com a Política de Privacidade da plataforma.
            </label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!consented || loading} className="gap-2">
            {loading ? null : <Rocket className="h-4 w-4" />}
            {loading ? "Publicando..." : "Confirmar e Publicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
